import { enumFieldType, enumObjClassNames, enumProfileRowType } from "../constants";
import { isInStandardSubmode } from "../utils/isInStandardSubmode.js";
import { isInViewer } from "../utils/isInViewer";
import { QProfileRow } from "./QProfileRow";
import { QXMLObject } from "./QXMLObject";
import { Globals } from "./Globals";

/**
 * @typedef {import('./QObjDBFieldDef').QObjDBFieldDef} QObjDBFieldDef
 * @typedef {import('./QXDoc').QXDoc} QXDoc
 */
export class QProfileMgr extends QXMLObject {
  constructor() {
    super();
    this.fProfileRowList = null;
    this.fProfilePositionRowList = null;
    this.fStarFields = null;
    this.fDocMgr = null;
    this.fEnableStarring = true;
    this.fHeadingConfig = null;
  }

  LoadProfile(theDoc, profile) {
    // admin set of fields shown in profile card table

    const profileFields = profile?.shownFields?.employee?.Included ?? profile?.shownFields;

    if (profileFields && Array.isArray(profileFields)) {
      this.AddKeyFields(theDoc, enumObjClassNames.box, profileFields);
    } else {
      // default set of profile fields shown when not set by admin
      this.SetUpDefaultProfile(theDoc, ["Photo", "Name", "Title"], enumObjClassNames.box);
    }

    if (theDoc.IsPositionDocument()) {
      const profileFields =
        profile?.shownFields?.position?.Included ?? profile?.shownPositionFields;
      if (!theDoc.GetObjDB().GetClassByName(enumObjClassNames.position)) return;
      if (profileFields) {
        this.AddKeyFields(theDoc, enumObjClassNames.position, profileFields);
      } else {
        this.SetUpDefaultProfile(
          theDoc,
          [
            "Position Title",
            "Position Division",
            "_Position Count",
            "_Subordinate Positions",
            "_Direct Subordinate Positions",
            "_Peer Positions"
          ],
          enumObjClassNames.position
        );
      }
    }

    // admin set of default fields shown as starred in profile card
    const profileHeading = profile?.headingConfig?.employee;
    const starFields = profileHeading
      ? [
          profileHeading.favoriteField1,
          profileHeading.favoriteField2,
          profileHeading.favoriteField3
        ]
      : profile?.starFields;
    this.SetDefaultStarFields(theDoc, starFields);

    const docMgr = theDoc.GetDocMgr();
    const w = docMgr.fWSMgr;

    this.fHeadingConfig = profile?.headingConfig;

    w.BroadcastTopic("profileLoaded", theDoc);
  }

  InitProfileMgr(docMgr) {
    this.fProfileRowList = [];
    this.fProfilePositionRowList = [];
    this.fDocMgr = docMgr;
    this.SetXMLElementName("profilemgr");
  }

  GetDocMgr() {
    return this.fDocMgr;
  }

  GetDoc() {
    return this.fDocMgr.GetCurrentDoc();
  }

  GetObjDB() {
    var theDoc = this.GetDoc();
    return theDoc.GetObjDB();
  }

  ResetFieldDefs() {
    var l = this.fProfileRowList;
    var n = l.length;
    var docMgr = this.GetDocMgr();

    var invalidFields = [];

    for (var i = 0; i < n; i++) {
      var aRow = this.fProfileRowList[i];
      var fDef = aRow.GetFieldDef(); //i
      if (fDef != null) {
        var fieldID = fDef.GetFieldID();
        var newDef = this.GetFieldDefByID(fieldID, null);
        aRow.SetFieldDef(newDef);
        aRow.SetDocMgrRef(docMgr);
      } else if (aRow.GetRowType() == enumProfileRowType.standard) invalidFields.push(aRow);
    }

    //If a field got removed from the data, but is in the profile, we are about to have problems.
    for (var anInvalidFieldIdx in invalidFields) {
      var theFieldToRemove = invalidFields[anInvalidFieldIdx];
      var invalidFieldIdx = this.fProfileRowList.indexOf(theFieldToRemove);
      this.fProfileRowList.splice(invalidFieldIdx, 1);
    }
  }

  FindProfileRow(fieldDef) {
    var l = this.fProfileRowList;
    var n = l.length;
    for (var i = 0; i < n; i++) {
      var aRow = this.fProfileRowList[i];
      var fDef = aRow.GetFieldDef();
      if (fDef == fieldDef) return aRow;
    }

    return null;
  }

  RemoveField(fieldDef) {
    var rowDef = this.FindProfileRow(fieldDef);
    if (rowDef) this.RemoveRow(rowDef);
  }

  /**
   * @param {string} objClassName
   */
  GetRowList(objClassName) {
    if (objClassName === enumObjClassNames.position) {
      return this.fProfilePositionRowList;
    } else {
      return this.fProfileRowList;
    }
  }

  /**
   * @param {string} objClassName
   */
  ResetProfileMgr(objClassName) {
    const profileList = this.GetRowList(objClassName);
    profileList.splice(0, profileList.length);
  }

  /**
   * @param {string} fieldName
   * @param {string} objClassName
   * @param {QXDoc=} theDoc
   * @returns {(QObjDBFieldDef | null)}
   */
  GetFieldDefByName(fieldName, objClassName, theDoc) {
    if (theDoc == null) theDoc = this.GetDoc();

    var o = theDoc.GetObjDB(); //fObjDB;
    var theClass = o.GetClassByName(objClassName);
    var fieldDef = theClass.GetFieldDefByName(fieldName);
    if (!fieldDef) fieldDef = theClass.GetFieldDefByID(fieldName); // fallback - also handles default profile fields which are field IDs ("Name" and "Title")
    return fieldDef;
  }

  /**
   * @param {string} fieldID
   * @param {string} objClassName
   * @param {QXDoc=} theDoc
   * @returns {(QObjDBFieldDef | null)}
   */
  GetFieldDefByID(fieldID, objClassName, theDoc) {
    if (theDoc == null) theDoc = this.GetDoc();

    var o = theDoc.GetObjDB(); //fObjDB;
    var theClass = o.GetClassByName(objClassName);
    var fieldDef = theClass?.GetFieldDefByID(fieldID);

    return fieldDef;
  }

  /**
   *
   * @param {string} fieldName
   * @param {(string[] | null)} fieldList
   * @param {string} objClassName
   * @param {QXDoc} theDoc
   * @param {QProfileRow[]} targetList
   */
  FindAndAddRowByName(fieldName, fieldList, objClassName, theDoc, list) {
    var fieldDef = this.GetFieldDefByName(fieldName, objClassName, theDoc);
    if (fieldDef) {
      this.AddProfileRow(fieldDef, list);
      if (fieldList) {
        var index = fieldList.indexOf(fieldDef);
        if (index != -1) fieldList.splice(index, 1);
      }
    }
  }

  /**
   *
   * @param {string} fieldID
   * @param {(string[] | null)} fieldList
   * @param {string} objClassName
   * @param {QXDoc} theDoc
   * @param {QProfileRow[]} list
   */
  FindAndAddRow(fieldID, fieldList, objClassName, theDoc, list) {
    var fieldDef = this.GetFieldDefByID(fieldID, objClassName, theDoc);
    if (fieldDef) {
      var fieldID = fieldDef.GetFieldID();
      if (fieldID === "AlsoReportsTo") {
        this.AddProfileRowType(fieldDef, enumProfileRowType.object, list);
      } else {
        this.AddProfileRow(fieldDef, list);
      }

      if (fieldList) {
        var index = fieldList.indexOf(fieldDef);
        if (index != -1) fieldList.splice(index, 1);
      }
    }
  }

  GetDefaultFieldsForClass(docRef, objClassName) {
    const objDb = docRef.GetObjDB();
    const objClass = objDb.GetClassByName(objClassName);
    return docRef.GetFieldList2(true, true, true, false, undefined, undefined, objClass);
  }

  /**
   *
   * @param {QXDoc} docRef
   * @param {string} objClassName
   * @param {string[]} fields
   */
  AddKeyFields(docRef, objClassName, fields) {
    if (!fields) {
      return;
    }

    const fieldList = this.GetDefaultFieldsForClass(docRef, objClassName);
    this.ResetProfileMgr(objClassName);

    const targetList = this.GetRowList(objClassName);
    let fieldName;

    for (fieldName of fields) {
      this.FindAndAddRowByName(fieldName, fieldList, objClassName, docRef, targetList);
    }
  }

  SetDefaultStarFields(theDoc, starFields) {
    this.fStarFields = [];
    if (starFields == null) {
      // TODO: Change it to holder fields (when they exist).
      if (theDoc.IsPositionDocument()) return;
      for (const fieldId of ["Level", "Headcount", "Peers"]) {
        this.FindAndAddRow(fieldId, null, enumObjClassNames.box, theDoc, this.fStarFields);
      }
    } else {
      for (var i in starFields) {
        if (!starFields[i] || starFields[i].length === 0) continue;
        this.FindAndAddRowByName(
          starFields[i],
          null,
          enumObjClassNames.box,
          theDoc,
          this.fStarFields
        );
      }
    }
  }

  GetDefaultStarFields(theDoc) {
    var l = this.fStarFields;
    var fieldList = [];
    if (l == null) {
      this.SetDefaultStarFields(theDoc, l);
      return fieldList;
    }

    var n = l.length;
    for (var i = 0; i < n; i++) {
      var profileRow = l[i];
      var fieldDef = profileRow.GetFieldDef();
      if (fieldDef != null) {
        var fieldName = fieldDef.GetFieldName();
        fieldList.push(fieldName);
      }
    }
    return fieldList;
  }

  GetEnableStarring() {
    return (
      Globals._uiConfig.fEnableProfileStarring === true &&
      Globals._accountInfo.GetAccessibleFeature("profileFieldStarring")
    );
  }

  /**
   *
   * @param {QXDoc} theDoc
   * @param {string[]} defaultFieldIds
   * @param {string=} objClassName
   */
  SetUpDefaultProfile(theDoc, defaultFieldIds = [], objClassName = enumObjClassNames.box) {
    const fieldList = this.GetDefaultFieldsForClass(theDoc, objClassName);

    this.ResetProfileMgr(objClassName);

    const targetList = this.GetRowList(objClassName);

    for (const fieldId of defaultFieldIds) {
      this.FindAndAddRow(fieldId, fieldList, objClassName, theDoc, targetList);
    }

    const numFields = fieldList.length;
    for (var i = 0; i < numFields; i++) {
      var aFieldDef = fieldList[i];
      var aFieldID = aFieldDef.GetFieldID();
      if (aFieldID === "AlsoReportsTo") {
        this.AddProfileRowType(aFieldDef, enumProfileRowType.object, targetList);
      } else {
        this.AddProfileRow(aFieldDef, targetList);
      }
    }
  }

  // SetUpProfile(rowList) {
  //   // After a data refresh
  //   this.fProfileRowList = [];
  //   var n = rowList.length;
  //   for (var i = 0; i < n; i++) {
  //     var newRow = null;
  //     var origRow = rowList[i];
  //     var rowType = origRow.GetRowType();
  //     switch (rowType) {
  //       case enumProfileRowType.standard:
  //         var origFieldDef = origRow.GetFieldDef();
  //         if (!origFieldDef) break;
  //         var origFieldID = origFieldDef.GetFieldID();
  //         var fieldDef = this.GetFieldDefByID(origFieldID, null);

  //         if (fieldDef) newRow = this.AddProfileRow(fieldDef);
  //         //    else
  //         //        console.log("origFieldID:",origFieldID, " not found");

  //         break;
  //       case enumProfileRowType.freeTextSingle:
  //       case enumProfileRowType.freeTextDouble:
  //         newRow = this.AddFreeTextRow(rowType);
  //         break;
  //       case enumProfileRowType.object:
  //         var origFieldDef = origRow.GetFieldDef();
  //         if (!origFieldDef) break;
  //         var origFieldID = origFieldDef.GetFieldID();
  //         var fieldDef = this.GetFieldDefByID(origFieldID, null);

  //         if (fieldDef) newRow = this.AddProfileRowType(fieldDef, enumProfileRowType.object);
  //         break;
  //     }

  //     if (newRow) newRow.CopyFormat(origRow);
  //   }
  // }

  GetNumRows(objClassName = enumObjClassNames.box) {
    const targetList = this.GetRowList(objClassName);
    return targetList.length;
  }

  GetRow(index, objClassName = enumObjClassNames.box) {
    const targetList = this.GetRowList(objClassName);
    return targetList[index];
  }

  GetRowIndex(rowDef, objClassName = enumObjClassNames.box) {
    const targetList = this.GetRowList(objClassName);
    var index = targetList.indexOf(rowDef);
    return index;
  }

  AddFreeTextRow(rowType) {
    var aRow = new QProfileRow();
    aRow.InitProfileRow(rowType, null);
    this.fProfileRowList.push(aRow);
    this.fDocMgr.fWSMgr.SetDirty(true);

    return aRow;
  }

  /**
   *
   * @param {QObjDBFieldDef} fieldDef
   * @param {*} profileRowType
   * @param {QProfileRow[]} list
   * @returns {QProfileRow}
   */
  AddProfileRowType(fieldDef, profileRowType, list) {
    var docMgr = this.GetDocMgr();
    var aRow = new QProfileRow();
    aRow.InitProfileRow(profileRowType, fieldDef);
    aRow.SetDocMgrRef(docMgr);
    list.push(aRow);
    if (!(isInViewer() && isInStandardSubmode())) docMgr.fWSMgr.SetDirty(true);
    return aRow;
  }

  AddProfileRow(fieldDef, list = this.fProfileRowList) {
    var docMgr = this.GetDocMgr();
    var aRow = this.CreateProfileRow(fieldDef);
    list.push(aRow);
    //This operation was traditionally done in the Workbench manually, so would dirty the document. Servercharts/DB
    //charts automate this procedure.
    if (!Globals._accountInfo.ReadOnlyMode()) docMgr.SetDirty(true);
    return aRow;
  }

  CreateProfileRow(fieldDef) {
    var docMgr = this.GetDocMgr();
    var aRow = new QProfileRow();
    var fieldType = enumProfileRowType.standard;
    if (fieldDef.GetFieldType() === enumFieldType.object) fieldType = enumProfileRowType.object;
    aRow.InitProfileRow(fieldType, fieldDef);
    //   var dbRef = this.GetObjDB();
    aRow.SetDocMgrRef(docMgr); //dbRef
    return aRow;
  }

  RemoveRow(rowDef) {
    var index = this.fProfileRowList.indexOf(rowDef);
    if (index != -1) {
      this.fProfileRowList.splice(index, 1);
      this.fDocMgr.SetDirty(true);
    }
  }

  RemoveRowByIdx(rowIdx) {
    this.fProfileRowList.splice(rowIdx, 1);
    //this.fDocMgr.SetDirty(true);
  }

  ShuffleRow(rowDef, shuffleUp) {
    var index = this.GetRowIndex(rowDef);
    var n = this.GetNumRows();
    var newIndex;

    if (shuffleUp) newIndex = index - 1;
    else newIndex = index + 1;

    if (newIndex < 0) return;
    if (newIndex >= n) return;

    var t = this.fProfileRowList[newIndex];
    this.fProfileRowList[newIndex] = rowDef;
    this.fProfileRowList[index] = t;
    this.fDocMgr.SetDirty(true);
  }

  LoadFromJSON(JSON) {
    var rows = JSON["rows"];
    var docMgr = this.GetDocMgr();
    for (var row = 0; row < rows.length; row++) {
      var rowInfo = rows[row];
      var aRow = new QProfileRow();

      aRow.SetDocMgrRef(docMgr);
      aRow.LoadFromJSON(rowInfo);
      this.fProfileRowList.push(aRow);
    }
  }

  OutputToJSON(JSON) {
    JSON["profile"] = {};
    var profile = JSON["profile"];
    profile["rows"] = [];
    var n = this.GetNumRows();
    for (var i = 0; i < n; i++) {
      var r = this.GetRow(i);
      profile["rows"].push(r.OutputToJSON(JSON));
    }
  }

  OutputChildElements(stream) {
    var n = this.GetNumRows();
    for (var i = 0; i < n; i++) {
      var r = this.GetRow(i);
      r.OutputToXML(stream);
    }
  }

  LoadChildElements(objTree) {
    var childList = objTree.GetChildOfTypeList("profilerow");
    var numRows = childList.length;

    var docMgr = this.GetDocMgr();
    for (var i = 0; i < numRows; i++) {
      var rowInfo = childList[i];
      var aRow = new QProfileRow();

      aRow.SetDocMgrRef(docMgr);
      aRow.LoadFromXML(rowInfo);
      this.fProfileRowList.push(aRow);
    }
  }

  GetProfileList(className = enumObjClassNames.box) {
    var rowList = [];
    var n = this.GetNumRows(className);
    for (var i = 0; i < n; i++) {
      var r = this.GetRow(i, className);
      rowList.push(r);
    }

    return rowList;
  }

  UpdateHeadingConfig(headingConfig) {
    this.fHeadingConfig = headingConfig;
  }
}
